// frontend/src/features/tasks/TaskCompletionTable.tsx
import { useEffect, useMemo, useState } from 'react';
import { Table, Badge, Text, Group, Pagination, Paper, Center } from '@mantine/core';
import type { TaskCompletion } from '@/types/task';
import { TASK_TYPE_LABELS, TASK_TYPE_COLORS } from '@/types/task';

interface TaskCompletionTableProps {
  completions: TaskCompletion[];
  onRowClick: (completion: TaskCompletion) => void;
}

const PAGE_SIZE = 25;

export function TaskCompletionTable({ completions, onRowClick }: TaskCompletionTableProps) {
  const [currentPage, setCurrentPage] = useState(1);

  // Reset to first page when completions data changes (e.g. filters applied)
  useEffect(() => {
    setCurrentPage(1);
  }, [completions]);

  // Sort by date descending (most recent first)
  const sortedCompletions = useMemo(() => {
    return [...completions].sort((a, b) => {
      return new Date(b.completed_at).getTime() - new Date(a.completed_at).getTime();
    });
  }, [completions]);

  // Paginate
  const paginatedCompletions = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    return sortedCompletions.slice(start, end);
  }, [sortedCompletions, currentPage]);

  const totalPages = Math.ceil(sortedCompletions.length / PAGE_SIZE);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const truncateNotes = (notes: string | undefined, maxLength = 100) => {
    if (!notes) return '\u2014';
    if (notes.length <= maxLength) return notes;
    return notes.substring(0, maxLength) + '...';
  };

  if (completions.length === 0) {
    return (
      <Paper p="xl" withBorder>
        <Center>
          <Text c="dimmed">No completions found in this date range</Text>
        </Center>
      </Paper>
    );
  }

  return (
    <Paper withBorder>
      <Table highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Date</Table.Th>
            <Table.Th>Task Name</Table.Th>
            <Table.Th>Task Type</Table.Th>
            <Table.Th>Equipment</Table.Th>
            <Table.Th>Notes</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {paginatedCompletions.map((completion) => (
            <Table.Tr
              key={completion.id}
              onClick={() => onRowClick(completion)}
              style={{ cursor: 'pointer' }}
            >
              <Table.Td>{formatDate(completion.completed_at)}</Table.Td>
              <Table.Td>{completion.name || 'Unknown Task'}</Table.Td>
              <Table.Td>
                {completion.task_type ? (
                  <Badge
                    color={TASK_TYPE_COLORS[completion.task_type]}
                    variant="light"
                  >
                    {TASK_TYPE_LABELS[completion.task_type]}
                  </Badge>
                ) : (
                  '\u2014'
                )}
              </Table.Td>
              <Table.Td>{completion.equipment_name || '\u2014'}</Table.Td>
              <Table.Td>
                <Text size="sm" lineClamp={2}>
                  {truncateNotes(completion.notes)}
                </Text>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      {totalPages > 1 && (
        <Group justify="space-between" p="md">
          <Text size="sm" c="dimmed">
            Showing {(currentPage - 1) * PAGE_SIZE + 1}-
            {Math.min(currentPage * PAGE_SIZE, sortedCompletions.length)} of{' '}
            {sortedCompletions.length} completions
          </Text>
          <Pagination
            value={currentPage}
            onChange={setCurrentPage}
            total={totalPages}
          />
        </Group>
      )}
    </Paper>
  );
}
